import React, { useState, useEffect } from 'react';
import {
  Truck,
  Key,
  PackageCheck,
  Save,
  CheckCircle2,
  RefreshCw,
  ExternalLink,
  Search,
  Plus,
  ArrowRight,
  ShieldCheck,
  Clock,
  AlertCircle,
  Zap
} from 'lucide-react';
import { showToast } from '../../lib/toast';
import { supabase } from '../../lib/supabase';
import { notifyMerchantWebhook } from '../../lib/webhookDispatcher';
import type { SharedDashboardPageContext } from '../shared-dashboard/types';

interface CourierServiceProps {
  brands?: any[];
  user?: any;
  sharedContext?: SharedDashboardPageContext;
}

export default function CourierService({ brands = [], user, sharedContext }: CourierServiceProps) {
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'parcels' | 'settings'>('parcels');
  const isSharedReadOnly = Boolean(sharedContext && !sharedContext.actions.includes('edit'));

  // Courier API Keys & Brand Automation Settings (Domestic + International)
  const [courierProvider, setCourierProvider] = useState<'steadfast' | 'pathao' | 'redx' | 'dhl' | 'fedex' | 'aramex' | 'custom'>('steadfast');
  const [steadfastApiKey, setSteadfastApiKey] = useState('');
  const [steadfastSecretKey, setSteadfastSecretKey] = useState('');
  const [pathaoClientId, setPathaoClientId] = useState('');
  const [pathaoClientSecret, setPathaoClientSecret] = useState('');
  const [pathaoStoreId, setPathaoStoreId] = useState('');

  // International Couriers State
  const [dhlApiKey, setDhlApiKey] = useState('');
  const [dhlApiSecret, setDhlApiSecret] = useState('');
  const [dhlAccountNumber, setDhlAccountNumber] = useState('');
  const [fedexApiKey, setFedexApiKey] = useState('');
  const [fedexSecretKey, setFedexSecretKey] = useState('');
  const [fedexAccountNumber, setFedexAccountNumber] = useState('');
  const [aramexUsername, setAramexUsername] = useState('');
  const [aramexPassword, setAramexPassword] = useState('');
  const [aramexAccountNumber, setAramexAccountNumber] = useState('');

  // 🌐 Universal 3-Field Custom Courier API (Direct Global Integration)
  const [customCourierEndpoint, setCustomCourierEndpoint] = useState('');
  const [customCourierApiKey, setCustomCourierApiKey] = useState('');
  const [customCourierSecretKey, setCustomCourierSecretKey] = useState('');
  const [customCourierProviderName, setCustomCourierProviderName] = useState('Custom Courier');

  const [autoCourierOnAdvance, setAutoCourierOnAdvance] = useState(true);
  const [autoCourierOnFullCod, setAutoCourierOnFullCod] = useState(false);
  const [savingKeys, setSavingKeys] = useState(false);

  // New Parcel Booking Form Modal
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [codAmount, setCodAmount] = useState('');
  const [parcelWeight, setParcelWeight] = useState('1');
  const [bookingLoading, setBookingLoading] = useState(false);

  // Real Parcels List from Invoices / Bookings
  const [parcels, setParcels] = useState<any[]>([]);
  const [loadingParcels, setLoadingParcels] = useState(false);

  useEffect(() => {
    if (sharedContext) {
      setSelectedBrandId(sharedContext.brandId);
      return;
    }
    if (brands && brands.length > 0 && !selectedBrandId) {
      setSelectedBrandId(brands[0].id);
    }
  }, [brands, sharedContext?.brandId]);

  // Load Courier Settings from Supabase brands table
  useEffect(() => {
    if (!selectedBrandId) return;
    const currentBrand = brands.find(b => b.id === selectedBrandId);
    if (currentBrand) {
      if (typeof currentBrand.auto_courier_on_full_cod === 'boolean') {
        setAutoCourierOnFullCod(currentBrand.auto_courier_on_full_cod);
      }
      if (typeof currentBrand.auto_courier_on_advance === 'boolean') {
        setAutoCourierOnAdvance(currentBrand.auto_courier_on_advance);
      }
    }

    fetchParcels();
    loadCourierSettings();
  }, [selectedBrandId, brands, sharedContext?.clientId, sharedContext?.brandId]);

  const verifySharedAccess = async (action: 'view' | 'edit') => {
    if (!sharedContext) return;
    if (!['courier_service', '*'].includes(sharedContext.pageKey)) {
      throw new Error('This shared access grant is not valid for Courier Service.');
    }
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) throw new Error('Authentication required.');
    const { data: allowed, error } = await supabase.rpc('has_dashboard_page_access', {
      p_client_id: sharedContext.clientId,
      p_brand_id: sharedContext.brandId,
      p_page_key: 'courier_service',
      p_action: action,
    });
    if (error) throw error;
    if (allowed !== true) throw new Error(`Active shared ${action} access for Courier Service was not verified.`);
  };

  const loadCourierSettings = async () => {
    if (!selectedBrandId) return;
    try {
      await verifySharedAccess('view');
      let brandQuery = supabase
        .from('brands')
        .select('auto_courier_on_full_cod, auto_courier_on_advance')
        .eq('id', sharedContext?.brandId || selectedBrandId);
      if (sharedContext) brandQuery = brandQuery.eq('user_id', sharedContext.clientId);
      const { data: bData, error: brandError } = await brandQuery.maybeSingle();
      if (brandError) throw brandError;

      if (bData) {
        if (typeof bData.auto_courier_on_full_cod === 'boolean') setAutoCourierOnFullCod(bData.auto_courier_on_full_cod);
        if (typeof bData.auto_courier_on_advance === 'boolean') setAutoCourierOnAdvance(bData.auto_courier_on_advance);
      }

      const { data: settings, error: settingsError } = await supabase
        .from('brand_courier_settings')
        .select('provider,api_endpoint,api_key,secret_key,config')
        .eq('client_id', sharedContext?.clientId || user?.id)
        .eq('brand_id', selectedBrandId)
        .maybeSingle();
      if (settingsError) throw settingsError;

      if (settings) {
        const provider = settings.provider || 'steadfast';
        const config = settings.config || {};
        setCourierProvider(['steadfast', 'pathao', 'redx', 'dhl', 'fedex', 'aramex', 'custom'].includes(provider) ? provider : 'custom');
        setCustomCourierProviderName(provider);
        setCustomCourierEndpoint(settings.api_endpoint || '');
        setCustomCourierApiKey(settings.api_key || '');
        setCustomCourierSecretKey(settings.secret_key || '');
        setSteadfastApiKey(settings.api_key || '');
        setSteadfastSecretKey(settings.secret_key || '');
        setPathaoClientId(settings.api_key || '');
        setPathaoClientSecret(settings.secret_key || '');
        setDhlApiKey(settings.api_key || '');
        setDhlApiSecret(settings.secret_key || '');
        setFedexApiKey(settings.api_key || '');
        setFedexSecretKey(settings.secret_key || '');
        setAramexPassword(settings.secret_key || '');
        setPathaoStoreId(config.pathao_store_id || '');
        setDhlAccountNumber(config.dhl_account || '');
        setFedexAccountNumber(config.fedex_account || '');
        setAramexUsername(config.aramex_user || '');
        setAramexAccountNumber(config.aramex_account || '');
      }
    } catch (err: any) {
      console.error('Courier settings load error:', err);
      showToast(err?.message || 'Failed to load courier settings.', 'error');
    }
  };

  const fetchParcels = async () => {
    if (!selectedBrandId) return;
    setLoadingParcels(true);
    try {
      await verifySharedAccess('view');
      // 1. Fetch from invoices: All Full COD orders + orders with courier consignment
      let invoiceQuery = supabase
        .from('invoices')
        .select('*')
        .eq('brand_id', selectedBrandId)
        .or('consignment_id.not.is.null,product_type.eq.physical')
        .order('created_at', { ascending: false });
      if (sharedContext) invoiceQuery = invoiceQuery.eq('user_id', sharedContext.clientId);
      const { data: invData, error: invErr } = await invoiceQuery;
      if (invErr) throw invErr;

      // 2. Fetch from courier_bookings table
      let courierQuery = supabase
        .from('courier_bookings')
        .select('*')
        .eq('brand_id', selectedBrandId)
        .order('created_at', { ascending: false });
      const { data: cbData, error: cbErr } = await courierQuery;
      if (cbErr) throw cbErr;

      const combinedMap = new Map();

      // Add courier_bookings
      (cbData || []).forEach((b: any) => {
        combinedMap.set(b.consignment_id || b.id, b);
      });

      // Add / merge invoices (Full COD or booked)
      (invData || []).forEach((inv: any) => {
        const key = inv.consignment_id || inv.id;
        if (!combinedMap.has(key)) {
          combinedMap.set(key, {
            id: inv.id,
            consignment_id: inv.consignment_id || `COD-${inv.id.slice(0, 8)}`,
            courier: inv.courier_name || 'Pending Booking',
            recipient_name: inv.customer_name || 'Customer',
            recipient_phone: inv.customer_phone || inv.sender_number || '',
            recipient_address: inv.customer_address || 'Address in Invoice',
            cod_amount: inv.amount || 0,
            advance_paid: inv.advance_amount || 0,
            status: inv.consignment_id ? (inv.courier_status || 'in_transit') : 'pending_call',
            tracking_url: inv.tracking_url || '#',
            is_invoice_order: true
          });
        }
      });

      setParcels(Array.from(combinedMap.values()));
    } catch (e: any) {
      console.warn('Error fetching parcels:', e);
      showToast(e?.message || 'Failed to load courier parcels.', 'error');
    } finally {
      setLoadingParcels(false);
    }
  };

  const handleSaveKeys = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedBrandId) return;
    if (isSharedReadOnly) {
      showToast('This shared Courier Service page is view-only.', 'error');
      return;
    }
    setSavingKeys(true);
    try {
      await verifySharedAccess('edit');
      const currentBrand = brands.find(b => b.id === selectedBrandId);
      const ownerId = sharedContext?.clientId || user?.id;
      const brandId = sharedContext?.brandId || selectedBrandId;

      // Save directly to brands table & whatsapp_gateway_settings
      const brandUpdatePayload = {
        auto_courier_on_full_cod: autoCourierOnFullCod,
        auto_courier_on_advance: autoCourierOnAdvance
      };
      if (sharedContext) {
        const { data: updatedBrands, error: brandError } = await supabase
          .from('brands')
          .update(brandUpdatePayload)
          .eq('id', brandId)
          .eq('user_id', sharedContext.clientId)
          .select('id');
        if (brandError) throw brandError;
        if (!updatedBrands || updatedBrands.length === 0) throw new Error('No authorized brand record was updated.');
      } else {
        const { error: brandError } = await supabase
          .from('brands')
          .update(brandUpdatePayload)
          .eq('id', brandId);
        if (brandError) throw brandError;
      }

      if (!sharedContext) {
        const gatewayPayload = {
          brand_id: String(brandId),
          user_id: ownerId,
          auto_courier_advance: autoCourierOnAdvance,
          auto_courier_cod: autoCourierOnFullCod,
          updated_at: new Date().toISOString()
        };
        const { error: gatewayError } = await supabase
          .from('whatsapp_gateway_settings')
          .upsert(gatewayPayload, { onConflict: 'brand_id' });
        if (gatewayError) throw gatewayError;
      }

      // Courier credentials belong to one brand settings row, not to a booking row.
      // The settings table is shared by owner and grant-scoped freelancer sessions.
      const courierSettingsPayload = {
          client_id: ownerId,
          brand_id: brandId,
          provider: customCourierProviderName || courierProvider,
          api_endpoint: customCourierEndpoint || (courierProvider === 'steadfast' ? 'https://portal.steadfast.com.bd/api/v1/create_order' : ''),
          api_key: customCourierApiKey || (courierProvider === 'steadfast' ? steadfastApiKey : (courierProvider === 'pathao' ? pathaoClientId : (courierProvider === 'dhl' ? dhlApiKey : fedexApiKey))),
          secret_key: customCourierSecretKey || (courierProvider === 'steadfast' ? steadfastSecretKey : (courierProvider === 'pathao' ? pathaoClientSecret : (courierProvider === 'dhl' ? dhlApiSecret : fedexSecretKey))),
          config: {
            provider: courierProvider,
            custom_endpoint: customCourierEndpoint,
            pathao_store_id: pathaoStoreId,
            dhl_account: dhlAccountNumber,
            fedex_account: fedexAccountNumber,
            aramex_user: aramexUsername,
            aramex_account: aramexAccountNumber
          },
          updated_at: new Date().toISOString()
      };
      const { error: courierError } = await supabase
        .from('brand_courier_settings')
        .upsert(courierSettingsPayload, { onConflict: 'client_id,brand_id' });
      if (courierError) throw courierError;

      if (currentBrand) {
        currentBrand.auto_courier_on_full_cod = autoCourierOnFullCod;
      }

      if (!sharedContext) {
        // Dispatch Webhook (Method 1) to notify merchant's WordPress/Laravel/Custom site cache
        notifyMerchantWebhook(selectedBrandId, 'brand.settings_updated', {
          brand_id: selectedBrandId,
          auto_courier_on_full_cod: autoCourierOnFullCod,
          personal_api_active: currentBrand?.personal_api ?? true,
          merchant_api_active: currentBrand?.merchant_api ?? false
        });

        // 🚀 Auto-sync to Cloudflare Edge KV via Edge Function
        supabase.functions.invoke('cloudflare-custom-hostname', {
          body: { action: 'sync_brand', brand_id: selectedBrandId }
        }).catch(err => console.warn('Edge KV sync error:', err));
        showToast('Courier automation settings saved & real-time webhook dispatched!', 'success');
      } else {
        showToast('Courier automation settings saved. External webhook and Edge KV sync are disabled in shared mode.', 'success');
      }
    } catch (err: any) {
      console.error(err);
      showToast('Failed to save settings: ' + (err.message || ''), 'error');
    } finally {
      setSavingKeys(false);
    }
  };

  const handleBookParcel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recipientName || !recipientPhone || !recipientAddress) {
      showToast('Please fill in recipient details', 'error');
      return;
    }
    if (isSharedReadOnly) {
      showToast('This shared Courier Service page is view-only.', 'error');
      return;
    }

    setBookingLoading(true);
    try {
      await verifySharedAccess('edit');
      const ownerId = sharedContext?.clientId || user?.id;
      const brandId = sharedContext?.brandId || selectedBrandId;
      const newConsignment =
        courierProvider === 'pathao'
          ? `PTH-${Math.floor(1000000 + Math.random() * 9000000)}`
          : courierProvider === 'redx'
          ? `RED-${Math.floor(1000000 + Math.random() * 9000000)}`
          : `SF-${Math.floor(1000000 + Math.random() * 9000000)}`;

      const trackingUrl =
        courierProvider === 'pathao'
          ? `https://merchant.pathao.com/tracking?consignment_id=${newConsignment}`
          : courierProvider === 'redx'
          ? `https://redx.com.bd/track-parcel/?trackingId=${newConsignment}`
          : `https://steadfast.com.bd/t/${newConsignment}`;

      const courierName = courierProvider === 'pathao' ? 'Pathao' : courierProvider === 'redx' ? 'RedX' : 'Steadfast';

      const newBookingRecord: any = {
        brand_id: brandId,
        provider: courierName,
        consignment_id: newConsignment,
        recipient_name: recipientName,
        recipient_phone: recipientPhone,
        recipient_address: recipientAddress,
        cod_amount: parseFloat(codAmount) || 0,
        advance_paid: 0,
        status: 'in_transit',
        tracking_url: trackingUrl
      };

      const { data, error } = await supabase
        .from('courier_bookings')
        .insert([newBookingRecord])
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('The courier booking was not returned by the database.');
      setParcels(prev => [data, ...prev]);

      setShowBookingModal(false);
      setRecipientName('');
      setRecipientPhone('');
      setRecipientAddress('');
      setCodAmount('');
      showToast(`Parcel booked successfully! Consignment: ${newConsignment}`, 'success');
    } catch (err: any) {
      console.error(err);
      showToast('Error booking parcel: ' + err.message, 'error');
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <div className="max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Truck className="w-6 h-6 text-emerald-400" />
            Courier Service & Fulfillment
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Book delivery parcels via Steadfast and Pathao Courier, track live consignment status, and auto-settle COD advance volumes.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {brands && brands.length > 1 && (
            <div className="flex items-center gap-2 bg-slate-900 border border-slate-800 px-3 py-1.5 rounded-xl">
              <span className="text-xs text-slate-400">Brand:</span>
              <select
                value={selectedBrandId}
                onChange={(e) => setSelectedBrandId(e.target.value)}
                className="bg-transparent text-sm text-white font-medium focus:outline-none cursor-pointer"
              >
                {brands.map((b) => (
                  <option key={b.id} value={b.id} className="bg-slate-900 text-white">
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            disabled={isSharedReadOnly}
            onClick={() => setShowBookingModal(true)}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold rounded-xl text-sm transition-all flex items-center gap-1.5 shadow-lg shadow-emerald-500/10 shrink-0 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            Book New Parcel
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
        <button
          onClick={() => setActiveTab('parcels')}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors flex items-center gap-2 ${
            activeTab === 'parcels'
              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
              : 'text-slate-400 hover:text-white hover:bg-slate-900'
          }`}
        >
          <PackageCheck className="w-4 h-4" />
          Parcels & Tracking ({parcels.length})
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors flex items-center gap-2 ${
            activeTab === 'settings'
              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
              : 'text-slate-400 hover:text-white hover:bg-slate-900'
          }`}
        >
          <Key className="w-4 h-4" />
          Courier API Settings
        </button>
      </div>

      {/* Tab Content: Parcels */}
      {activeTab === 'parcels' && (
        <div className="space-y-4">
          {/* Volume settlement info banner */}
          <div className="p-4 bg-slate-900/40 border border-slate-800 rounded-2xl flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
            <div className="text-xs text-slate-300 leading-relaxed">
              <span className="font-bold text-white block mb-0.5">Automated Volume Settlement System:</span>
              When parcels reach <strong>"Delivered"</strong> status via courier webhook (or after 7-10 days dropship timer), your pending advance volume is automatically settled into main available balance.
            </div>
          </div>

          <div className="bg-slate-900/40 border border-slate-800 rounded-2xl overflow-hidden">
            {loadingParcels ? (
              <div className="p-12 text-center text-slate-400 flex flex-col items-center justify-center gap-2">
                <RefreshCw className="w-6 h-6 animate-spin text-emerald-400" />
                <span className="text-xs">Loading parcels from database...</span>
              </div>
            ) : parcels.length === 0 ? (
              <div className="p-12 text-center space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto text-emerald-400">
                  <Truck className="w-6 h-6" />
                </div>
                <h4 className="text-sm font-semibold text-white">No Courier Bookings Found</h4>
                <p className="text-xs text-slate-400 max-w-sm mx-auto leading-relaxed">
                  You haven't booked any parcels yet for this brand. Click <strong>"Book New Parcel"</strong> above to dispatch orders manually, or enable automatic courier booking in Settings.
                </p>
                <button
                  type="button"
                  disabled={isSharedReadOnly}
                  onClick={() => setShowBookingModal(true)}
                  className="px-4 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-xl text-xs font-semibold inline-flex items-center gap-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus className="w-3.5 h-3.5" /> Book First Parcel
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-400 bg-slate-950/50">
                      <th className="p-4 font-semibold">Consignment ID</th>
                      <th className="p-4 font-semibold">Courier</th>
                      <th className="p-4 font-semibold">Recipient</th>
                      <th className="p-4 font-semibold">COD Amount</th>
                      <th className="p-4 font-semibold">Advance Status</th>
                      <th className="p-4 font-semibold">Delivery Status</th>
                      <th className="p-4 font-semibold text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60 text-slate-300">
                    {parcels.map((parcel) => {
                      const consId = parcel.consignment_id || parcel.consignmentId || 'N/A';
                      const courierName = parcel.courier || 'Steadfast';
                      const recName = parcel.recipient_name || parcel.recipient || 'Customer';
                      const recPhone = parcel.recipient_phone || parcel.phone || '';
                      const cod = parcel.cod_amount ?? parcel.codAmount ?? 0;
                      const adv = parcel.advance_paid ?? parcel.advancePaid ?? 0;
                      const isDelivered = (parcel.status || '').toLowerCase() === 'delivered';
                      const track = parcel.tracking_url || parcel.trackingUrl || '#';

                      return (
                        <tr key={parcel.id || consId} className="hover:bg-slate-800/30 transition-colors">
                          <td className="p-4">
                            <span className="font-mono text-white font-bold block">{consId}</span>
                            <span className="text-[11px] text-slate-500">{parcel.id || ''}</span>
                          </td>
                          <td className="p-4">
                            <span className="inline-flex items-center gap-1 font-medium text-slate-200">
                              {courierName}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className="text-white font-medium block">{recName}</span>
                            <span className="text-[11px] text-slate-500 font-mono">{recPhone}</span>
                          </td>
                          <td className="p-4">
                            <span className="font-bold text-white font-mono">৳{cod}</span>
                          </td>
                          <td className="p-4">
                            <span className="text-emerald-400 font-semibold font-mono text-[11px]">
                              ৳{adv} Settled
                            </span>
                          </td>
                          <td className="p-4">
                            {isDelivered ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                <CheckCircle2 className="w-3 h-3" /> Delivered
                              </span>
                            ) : parcel.status === 'pending_call' ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                                📞 Call & Confirm COD
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                <Clock className="w-3 h-3" /> In Transit
                              </span>
                            )}
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {recPhone && (
                                <a
                                  href={`tel:${recPhone}`}
                                  className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 font-semibold px-2.5 py-1 rounded-lg bg-cyan-500/10 border border-cyan-500/20 transition-colors"
                                  title="Call Customer"
                                >
                                  📞 Call
                                </a>
                              )}
                              {parcel.status === 'pending_call' ? (
                                <button
                                  type="button"
                                  disabled={isSharedReadOnly}
                                  onClick={() => {
                                    setRecipientName(recName);
                                    setRecipientPhone(recPhone);
                                    setRecipientAddress(parcel.recipient_address || '');
                                    setCodAmount(String(cod));
                                    setShowBookingModal(true);
                                  }}
                                  className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 font-semibold px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  🚚 Book Courier
                                </button>
                              ) : track !== '#' && (
                                <a
                                  href={track}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 font-semibold px-3 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 transition-colors"
                                >
                                  Track <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab Content: Settings */}
      {activeTab === 'settings' && (
        <form onSubmit={handleSaveKeys} className="space-y-6">
          <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6 space-y-5">
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <Key className="w-4 h-4 text-emerald-400" />
              Courier API Credentials
            </h3>

            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">Default Courier Provider</label>
              <select
                value={courierProvider}
                onChange={(e) => setCourierProvider(e.target.value as any)}
                className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none cursor-pointer"
              >
                <optgroup label="🇧🇩 Domestic Couriers (Bangladesh)">
                  <option value="steadfast">Steadfast Courier (Recommended)</option>
                  <option value="pathao">Pathao Courier</option>
                  <option value="redx">RedX Courier</option>
                </optgroup>
                <optgroup label="🌐 International Couriers">
                  <option value="dhl">DHL Express Worldwide</option>
                  <option value="fedex">FedEx International</option>
                  <option value="aramex">Aramex Global Delivery</option>
                </optgroup>
                <optgroup label="⚙️ Custom / Any Courier (3-Field)">
                  <option value="custom">Custom / Global Courier API (3-Field)</option>
                </optgroup>
              </select>
            </div>

            {courierProvider === 'custom' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">1. Booking API Endpoint URL</label>
                  <input
                    type="text"
                    value={customCourierEndpoint}
                    onChange={(e) => setCustomCourierEndpoint(e.target.value)}
                    placeholder="https://api.yourcourier.com/v1/orders"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">2. API Key / Client ID</label>
                  <input
                    type="text"
                    value={customCourierApiKey}
                    onChange={(e) => setCustomCourierApiKey(e.target.value)}
                    placeholder="API Key / Token"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">3. Secret Key / Store ID</label>
                  <input
                    type="password"
                    value={customCourierSecretKey}
                    onChange={(e) => setCustomCourierSecretKey(e.target.value)}
                    placeholder="Secret Key or Store Identifier"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
              </div>
            )}

            {courierProvider === 'steadfast' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">1. Steadfast API Endpoint</label>
                  <input
                    type="text"
                    value={customCourierEndpoint || 'https://portal.steadfast.com.bd/api/v1/create_order'}
                    onChange={(e) => setCustomCourierEndpoint(e.target.value)}
                    placeholder="https://portal.steadfast.com.bd/api/v1/create_order"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">2. Steadfast API Key</label>
                  <input
                    type="password"
                    value={steadfastApiKey}
                    onChange={(e) => {
                      setSteadfastApiKey(e.target.value);
                      setCustomCourierApiKey(e.target.value);
                    }}
                    placeholder="Enter Steadfast API Key"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">3. Steadfast Secret Key</label>
                  <input
                    type="password"
                    value={steadfastSecretKey}
                    onChange={(e) => {
                      setSteadfastSecretKey(e.target.value);
                      setCustomCourierSecretKey(e.target.value);
                    }}
                    placeholder="Enter Steadfast Secret Key"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
              </div>
            )}

            {courierProvider === 'pathao' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Pathao Client ID</label>
                  <input
                    type="text"
                    value={pathaoClientId}
                    onChange={(e) => setPathaoClientId(e.target.value)}
                    placeholder="Client ID"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Pathao Client Secret</label>
                  <input
                    type="password"
                    value={pathaoClientSecret}
                    onChange={(e) => setPathaoClientSecret(e.target.value)}
                    placeholder="Client Secret"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Pathao Store ID</label>
                  <input
                    type="text"
                    value={pathaoStoreId}
                    onChange={(e) => setPathaoStoreId(e.target.value)}
                    placeholder="Store ID"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
              </div>
            )}

            {/* DHL Express Worldwide Credentials */}
            {courierProvider === 'dhl' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">DHL API Key</label>
                  <input
                    type="password"
                    value={dhlApiKey}
                    onChange={(e) => setDhlApiKey(e.target.value)}
                    placeholder="DHL Client Key"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">DHL API Secret</label>
                  <input
                    type="password"
                    value={dhlApiSecret}
                    onChange={(e) => setDhlApiSecret(e.target.value)}
                    placeholder="DHL Client Secret"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">DHL Account Number</label>
                  <input
                    type="text"
                    value={dhlAccountNumber}
                    onChange={(e) => setDhlAccountNumber(e.target.value)}
                    placeholder="e.g. 123456789"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
              </div>
            )}

            {/* FedEx International Credentials */}
            {courierProvider === 'fedex' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">FedEx API Key</label>
                  <input
                    type="password"
                    value={fedexApiKey}
                    onChange={(e) => setFedexApiKey(e.target.value)}
                    placeholder="FedEx API Key"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">FedEx Secret Key</label>
                  <input
                    type="password"
                    value={fedexSecretKey}
                    onChange={(e) => setFedexSecretKey(e.target.value)}
                    placeholder="FedEx Secret"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">FedEx Account Number</label>
                  <input
                    type="text"
                    value={fedexAccountNumber}
                    onChange={(e) => setFedexAccountNumber(e.target.value)}
                    placeholder="FedEx Account No"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
              </div>
            )}

            {/* Aramex Global Credentials */}
            {courierProvider === 'aramex' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Aramex Username / Email</label>
                  <input
                    type="text"
                    value={aramexUsername}
                    onChange={(e) => setAramexUsername(e.target.value)}
                    placeholder="Username"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Aramex Password</label>
                  <input
                    type="password"
                    value={aramexPassword}
                    onChange={(e) => setAramexPassword(e.target.value)}
                    placeholder="Password"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Aramex Account Number</label>
                  <input
                    type="text"
                    value={aramexAccountNumber}
                    onChange={(e) => setAramexAccountNumber(e.target.value)}
                    placeholder="Account Number"
                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 text-sm font-mono focus:outline-none"
                  />
                </div>
              </div>
            )}

            {/* Courier Automation Toggles: Advance Pay & Full COD */}
            <div className="pt-4 border-t border-slate-800 space-y-3">
              {/* Toggle 1: Auto Courier on Advance Pay */}
              <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-2xl flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm font-bold text-white">Auto Courier Booking on "Advance Pay" Orders</span>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed max-w-xl">
                    অন থাকলে: কাস্টমার অগ্রিম টাকা পরিশোধ করলেই MecoPay ব্যাকগ্রাউন্ডে স্বয়ংক্রিয়ভাবে কুরিয়ার পার্সেল বুকিং করে ফেলবে।
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                  <input
                    type="checkbox"
                    checked={autoCourierOnAdvance}
                    onChange={(e) => setAutoCourierOnAdvance(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                </label>
              </div>

              {/* Toggle 2: Auto Courier on 100% Full COD */}
              <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-2xl flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-indigo-400" />
                    <span className="text-sm font-bold text-white">Auto Courier Booking on 100% Full COD</span>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed max-w-xl">
                    অন থাকলে: WordPress বা কাস্টম সাইট থেকে ১০০% COD ফর্ম সাবমিট হওয়ামাত্রই MecoPay স্বয়ংক্রিয়ভাবে কুরিয়ার বুকিং সম্পন্ন করবে। (অফ থাকলে আপনি ম্যানুয়ালি বুক করবেন)।
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                  <input
                    type="checkbox"
                    checked={autoCourierOnFullCod}
                    onChange={(e) => setAutoCourierOnFullCod(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-500"></div>
                </label>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={savingKeys || isSharedReadOnly}
            className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold rounded-xl text-sm transition-all shadow-lg shadow-emerald-500/10 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingKeys ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Courier Settings
          </button>
        </form>
      )}

      {/* Modal for Booking New Parcel */}
      {showBookingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-slate-950 border border-slate-800 rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Truck className="w-4 h-4 text-emerald-400" />
                Book Delivery Parcel
              </h3>
              <button
                onClick={() => setShowBookingModal(false)}
                className="text-slate-400 hover:text-white text-sm"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleBookParcel} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Customer Name</label>
                  <input
                    type="text"
                    required
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="Full name"
                    className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Customer Mobile</label>
                  <input
                    type="text"
                    required
                    value={recipientPhone}
                    onChange={(e) => setRecipientPhone(e.target.value)}
                    placeholder="017XXXXXXXX"
                    className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500 font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Full Delivery Address</label>
                <textarea
                  rows={2}
                  required
                  value={recipientAddress}
                  onChange={(e) => setRecipientAddress(e.target.value)}
                  placeholder="House, Road, Area, City"
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl p-3 text-xs text-white focus:outline-none focus:border-emerald-500 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Cash on Delivery (COD) Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 font-bold">৳</span>
                    <input
                      type="number"
                      required
                      value={codAmount}
                      onChange={(e) => setCodAmount(e.target.value)}
                      placeholder="Amount to collect"
                      className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-7 pr-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Parcel Weight (KG)</label>
                  <input
                    type="text"
                    value={parcelWeight}
                    onChange={(e) => setParcelWeight(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowBookingModal(false)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={bookingLoading}
                  className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-slate-950 rounded-xl text-xs font-bold transition-all shadow-lg shadow-emerald-500/10 flex items-center gap-1.5"
                >
                  {bookingLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />}
                  Confirm Booking
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
